import { useState, useEffect, useCallback, useRef } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  Box,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { DirectionsCar, CheckCircle, Close } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { supabase } from "../../supabaseClient";
import { motion, AnimatePresence } from "framer-motion";

const theme = createTheme({
  typography: {
    fontSize: 20,
    h1: { fontSize: "3.5rem", fontWeight: 700 },
    h2: { fontSize: "3rem", fontWeight: 600 },
    h3: { fontSize: "2.5rem", fontWeight: 600 },
    h4: { fontSize: "2rem", fontWeight: 500 },
    h5: { fontSize: "1.75rem", fontWeight: 500 },
    h6: { fontSize: "1.5rem", fontWeight: 500 },
    body1: { fontSize: "1.35rem" },
    body2: { fontSize: "1.2rem" },
    button: { fontSize: "1.2rem", fontWeight: 600, textTransform: "none" },
  },
});

/* -----------------------------------------
   FLOOR CONFIG (Edit anytime)
------------------------------------------*/

// 1️⃣ MAIN JSON → Controls UI Label + Order
export const FLOOR_NAME_MAP = {
  0: "Basement - 01",
  1: "Lower Ground floor",
  2: "UG",
  3: "P2",
};

// 2️⃣ AUTO GENERATED REVERSE MAP → (label → index)
//     Helps in sorting by floor order
export const FLOOR_INDEX_MAP = Object.fromEntries(
  Object.entries(FLOOR_NAME_MAP).map(([index, label]) => [
    label.toLowerCase(), // normalize for safe comparison
    Number(index),
  ])
);

// 3️⃣ Helper: Get display label from raw DB value
export const getFloorLabel = (rawName) => {
  if (!rawName) return "Other";

  const normalized = rawName.toLowerCase().trim();

  // If found in reverse map → return clean UI label
  if (FLOOR_INDEX_MAP[normalized] !== undefined) {
    const idx = FLOOR_INDEX_MAP[normalized];
    return FLOOR_NAME_MAP[idx];
  }

  // If floor not defined in JSON → return original DB label
  return rawName;
};

function ParkingModal({ isOpen, onClose, allotment }) {
  console.log("allotment", allotment);
  const [parkingSlots, setParkingSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedParkingNumber, setSelectedParkingNumber] = useState("");
  const [selectedUpgradeNumber, setSelectedUpgradeNumber] = useState("");
  const [directUpgrade, setDirectUpgrade] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allottedParking, setAllottedParking] = useState([]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [oldParkingNumber, setOldParkingNumber] = useState(null);
  const [newParkingNumber, setNewParkingNumber] = useState(null);
  // Store the last confirmed parking number (from DB or after user confirms)
  const [confirmedParkingNumber, setConfirmedParkingNumber] = useState(null);
  const [upgradedParkingSlots, setUpgradedParkingSlots] = useState([]);
  const [showBlockIncludedDialog, setShowBlockIncludedDialog] = useState(false);
  const [showBlockUpgradeDialog, setShowBlockUpgradeDialog] = useState(false);
  const [showAlreadyAllottedDialog, setShowAlreadyAllottedDialog] =
    useState(false);
  const [conflictParkingNumber, setConflictParkingNumber] = useState(null);
  const [includedPage, setIncludedPage] = useState(1);
  const [upgradedPage, setUpgradedPage] = useState(1);

  const [showUpgradeUnavailableDialog, setShowUpgradeUnavailableDialog] =
    useState(false);
  const [upgradeUnavailableParkingName, setUpgradeUnavailableParkingName] =
    useState("");
  const [oldParkingTypeId, setOldParkingTypeId] = useState("");

  const [oldParkingCost, setOldParkingCost] = useState(0);
  const [oldParkingCase, setOldParkingCase] = useState(null); // 'null' initially until data is fetched
  const [allotedParkingName, setAllottedParkingName] = useState(null);

  const [unitCost, setUnitCost] = useState(null);
  const [isSummaryChecked, setIsSummaryChecked] = useState(false);
  const pricingRef = useRef(null);

  // 🔹 Multi-parking selection state
  const [selectedIncludedSlots, setSelectedIncludedSlots] = useState([]);
  const [selectedUpgradeSlots, setSelectedUpgradeSlots] = useState([]);

  // 🔹 Parking rule engine (derived from config)
  const [parkingRules, setParkingRules] = useState({
    maxIncluded: 0,
    maxUpgrade: 0,
    allowUpgrade: false,
  });

  const ITEMS_PER_PAGE = 12;

  const projectId = allotment?.projectId || allotment?.projectid;
  const wingId = allotment?.wingId || allotment?.wingid;
  const unitId = allotment?.unitId || allotment?.unitid;
  const allotmentPaymentId =
    allotment?.allotmentpaymentid || allotment?.allotmentpaymentid;
  console.log("allotmentPayment Id =====>?>", allotmentPaymentId);

  useEffect(() => {
    const loadAllottedParking = async () => {
      if (!isOpen || !allotmentPaymentId) return;

      try {
        const data = await fetchAllottedParking(allotmentPaymentId);

        setAllottedParking(data);
        if (data.length > 0) {
          setConfirmedParkingNumber(data.map((p) => p.parkingNumber));
        }
      } catch (err) {
        console.error("❌ Error loading allotted parking:", err);
        setAllottedParking(null);
        setConfirmedParkingNumber(null);
        setAllottedParkingName(null);
      }
    };

    loadAllottedParking();
  }, [isOpen, allotmentPaymentId]);

  const fetchParkingSlots = async () => {
    if (!projectId || !wingId || !unitId) {
      console.warn("Missing required identifiers:", {
        projectId,
        wingId,
        unitId,
      });
      return;
    }

    setLoading(true);

    try {
      // ------------------------------------------------------------------
      // 1️⃣ FETCH INVENTORY TYPE FOR UNIT
      // ------------------------------------------------------------------
      const { data: invData, error: invError } = await supabase
        .from("ProjectInventoryRelations")
        .select(
          `
        unitId (
          inventoryTypeId,
          InventoryType (type)
        )
      `
        )
        .eq("unitId", unitId)
        .single();

      if (invError) throw invError;

      const inventoryTypeId = invData?.unitId?.inventoryTypeId;
      if (!inventoryTypeId) {
        console.warn("Inventory type not found for unit:", unitId);
        setParkingSlots([]);
        setUpgradedParkingSlots([]);
        return;
      }

      // ------------------------------------------------------------------
      // 2️⃣ FETCH CONFIG & DERIVE RULES
      // ------------------------------------------------------------------
      const { data: mappingData, error: mappingError } = await supabase
        .from("UnitType_ParkingType_Config")
        .select("ParkingTypeId, isUpgrade, IsUpgradeEligible")
        .eq("ProjectId", projectId)
        .eq("WingId", wingId)
        .eq("UnitTypeId", inventoryTypeId);

      if (mappingError) throw mappingError;

      if (!mappingData || mappingData.length === 0) {
        console.warn("No parking config found for this unit type.");
        setParkingSlots([]);
        setUpgradedParkingSlots([]);
        setParkingRules({
          maxIncluded: 0,
          maxUpgrade: 0,
          allowUpgrade: false,
        });
        return;
      }

      // 🔹 INCLUDED CONFIG
      const includedConfigs = mappingData.filter((r) => r.isUpgrade === false);
      const includedParkingTypeIds = includedConfigs.map(
        (r) => r.ParkingTypeId
      );

      // 🔹 UPGRADE ELIGIBILITY
      const upgradeEligibleConfigs = includedConfigs.filter(
        (r) => r.IsUpgradeEligible === true
      );

      // 🔹 SET RULES
      setParkingRules({
        maxIncluded: includedConfigs.length,
        maxUpgrade: upgradeEligibleConfigs.length,
        allowUpgrade: upgradeEligibleConfigs.length > 0,
      });

      // ------------------------------------------------------------------
      // 3️⃣ FETCH INCLUDED PARKING (ONLY IF CONFIG EXISTS)
      // ------------------------------------------------------------------
      let formattedIncluded = [];

      if (includedParkingTypeIds.length > 0) {
        const { data: includedData, error: includedError } = await supabase
          .from("ParkingInventory")
          .select(
            `
          parkingInventoryId,
          parkingTypeId,
          parkingNumber,
          parkingCost,
          parkingStatusId,
          floorNumber,
          ParkingTypes (parkingType)
        `
          )
          .eq("projectId", projectId)
          .eq("wingId", wingId)
          .eq("parkingStatusId", 0)
          .in("parkingTypeId", includedParkingTypeIds);

        if (includedError) throw includedError;

        formattedIncluded = (includedData || []).map((slot) => ({
          id: slot.parkingInventoryId,
          number: slot.parkingNumber,
          type: slot.ParkingTypes?.parkingType || "Unknown",
          price: slot.parkingCost || 0,
          parkingTypeId: slot.parkingTypeId,
          isAvailable: true,
          floorNumber: slot.floorNumber,
        }));
      }

      // 🔐 IMPORTANT: explicitly set included slots
      setParkingSlots(formattedIncluded);

      // ------------------------------------------------------------------
      // 4️⃣ FETCH UPGRADED PARKING VIA RPC (AUTHORITATIVE)
      // ------------------------------------------------------------------
      const { data: upgradedData, error: upgradedError } = await supabase.rpc(
        "fetch_upgradable_parking_slots_final222222",
        {
          project_id: projectId,
          wing_id: wingId,
          unit_id: unitId,
        }
      );

      if (upgradedError) throw upgradedError;

      const formattedUpgraded = (upgradedData || []).map((slot) => ({
        id: slot.parkinginventoryid,
        number: slot.parkingnumber,
        type: slot.parkingtype || "Unknown",
        price: slot.parkingcost || 0,
        parkingTypeId: slot.parkingtypeid,
        isAvailable: slot.parkingstatusid === 0,
        floorName: slot.floorname || "Unknown",
      }));

      setUpgradedParkingSlots(formattedUpgraded);

      console.log("✅ Included Parking:", formattedIncluded);
      console.log("💎 Upgraded Parking:", formattedUpgraded);
      console.log("📏 Parking Rules:", {
        maxIncluded: includedConfigs.length,
        maxUpgrade: upgradeEligibleConfigs.length,
      });
    } catch (err) {
      console.error("❌ Error fetching parking slots:", err);
      setParkingSlots([]);
      setUpgradedParkingSlots([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParkingSlots();
  }, [projectId, wingId, unitId]);

  const getSelectedSlot = () =>
    parkingSlots.find((slot) => slot.number === selectedParkingNumber);

  const getUpgradeSlot = () =>
    parkingSlots.find((slot) => slot.number === selectedUpgradeNumber);

  const getTypeColor = (type) => {
    switch (type) {
      case "Double dependent stack":
        return "primary";
      case "Single covered parking":
        return "primary";
      case "Stack Parking":
        return "primary";
      case "Single Covered + EV":
        return "warning";
      case "Single Open parking":
        return "success";
      case "Double dependent stack + EV":
        return "primary";
      default:
        return "primary";
    }
  };

  useEffect(() => {
    const loadOldCost = async () => {
      if (!allottedParking?.parkingInventoryId) return;

      const { data } = await supabase
        .from("ParkingInventory")
        .select("parkingCost,parkingTypeId")
        .eq("parkingInventoryId", allottedParking.parkingInventoryId)
        .single();
      console.log("data", data);

      setOldParkingCost(data?.parkingCost || 0);
      setOldParkingTypeId(data?.parkingTypeId || "");
    };

    loadOldCost();
  }, [allottedParking]);

  const getOldParkingcase = async () => {
    if (!oldParkingTypeId) return;

    const { data: invData, error: invError } = await supabase
      .from("Inventory")
      .select(
        `
      inventoryTypeId,
      InventoryType (type)
    `
      )
      .eq("unitId", unitId)
      .single();

    if (invError) throw invError;
    if (!invData) {
      console.warn("No Inventory record found for unitId:", unitId);
      setParkingSlots([]);
      setLoading(false);
      return;
    }

    const inventoryTypeId = invData.inventoryTypeId;

    console.log("inventory type", inventoryTypeId);
    console.log("projectId", projectId);

    const { data: oldMappingData, error: oldMappingError } = await supabase
      .from("UnitType_ParkingType_Config")
      .select("isUpgrade")
      .eq("ProjectId", projectId)
      .eq("WingId", wingId)
      .eq("UnitTypeId", inventoryTypeId)
      .eq("ParkingTypeId", oldParkingTypeId) // Checking for the old parking type id
      .single(); // Ensures you get only one result since ParkingTypeId should be unique per combination

    if (oldMappingError) throw oldMappingError;

    if (!oldMappingData) {
      console.warn("No mapping found for oldParkingTypeId.");
      return;
    }

    const isUpgraded = oldMappingData.isUpgrade;

    // Store the case in the state
    setOldParkingCase(isUpgraded ? "upgraded" : "included");

    if (isUpgraded) {
      console.log(
        `Old Parking Type ID ${oldParkingTypeId} is marked as upgraded.`
      );
    } else {
      console.log(`Old Parking Type ID ${oldParkingTypeId} is included.`);
    }

    setLoading(false);
  };

  const handleUpgradeSelect = (slot) => {
    const totalSelected =
      selectedIncludedSlots.length + selectedUpgradeSlots.length;

    // 🧪 Upgrade NOT allowed at all
    if (!parkingRules.allowUpgrade) {
      setShowUpgradeUnavailableDialog(true);
      return false; // ⛔ BLOCKED
    }

    // 🔁 Toggle OFF
    if (selectedUpgradeSlots.some((s) => s.id === slot.id)) {
      setSelectedUpgradeSlots((prev) => prev.filter((s) => s.id !== slot.id));
      return true; // ✅ allowed (toggle)
    }

    // 🚫 TOTAL limit reached
    if (totalSelected >= parkingRules.maxIncluded) {
      setShowBlockUpgradeDialog(true);
      return false; // ⛔ BLOCKED
    }

    // 🚫 Upgrade-specific limit
    if (selectedUpgradeSlots.length >= parkingRules.maxUpgrade) {
      setShowBlockUpgradeDialog(true);
      return false; // ⛔ BLOCKED
    }

    // ✅ ALLOW
    setSelectedUpgradeSlots((prev) => [...prev, slot]);
    return true;
  };

  const fetchUnitCost = async (allotmentPaymentId) => {
    const { data, error } = await supabase
      .from("AllotmentPayment")
      .select("unitCost")
      .eq("allotmentPaymentId", allotmentPaymentId)
      .single();

    if (error) {
      console.error("Error fetching unitCost:", error);
      return null;
    }

    return data?.unitCost || null;
  };

  useEffect(() => {
    if (!allotmentPaymentId) return;

    async function loadCost() {
      const cost = await fetchUnitCost(allotmentPaymentId);
      setUnitCost(cost);
    }

    loadCost();
  }, [allotmentPaymentId]);

  const calculatePricing = () => {
    if (!allotment?.unitcost) return { totalCost: 0 };

    if (!unitCost) return { totalCost: 0 };

    const existingBsp = Number(unitCost?.bsp || 0);
    const existingTotal = Number(unitCost?.total || 0);

    // Step 1: Identify the selected and previously selected (old) parking slots
    const upgradedSlot = upgradedParkingSlots.find(
      (slot) => slot.number === selectedUpgradeNumber
    );
    const includedSlot = parkingSlots.find(
      (slot) => slot.number === selectedParkingNumber
    );

    // Step 2: Determine the new selected slot and its type (included or upgraded)
    const newSlot = upgradedSlot || includedSlot; // Either the new selected upgraded or included slot

    // Step 3: Return early if no new or old slot is selected (fallback)
    if (!newSlot) {
      return { totalCost: existingTotal || 0 }; // Return existing total if no slot is selected
    }

    // Step 4: Detect the type of the new selected parking and the old parking
    const pricingTypeNew = upgradedSlot ? "upgrade" : "included"; // New parking type (either included or upgrade)

    // Step 5: Determine the cost for the allotted parking based on its type
    console.log("oldParkingcase", oldParkingCase);

    const allottedParkingCost =
      oldParkingCase === "included" ? 0 : oldParkingCost; // Set cost to 0 if it's included, else use the old parking cost
    console.log("allottedParkingCost", allottedParkingCost);

    // Step 6: Calculate pricing based on old and new parking costs
    const originalBsp = existingBsp - allottedParkingCost;
    console.log("existingBsp", existingBsp);

    console.log("originalBsp", originalBsp);
    // Base price excluding old parking cost

    // Old total (before parking change)
    const oldBsp = originalBsp + allottedParkingCost;
    const oldSdr = oldBsp * 0.07; // SDR (7%)
    const oldGst = oldBsp * 0.05; // GST (5%)
    const oldTotal = oldBsp + oldSdr + oldGst; // Old total before change

    // New total (after parking change)
    const upgradedCost = selectedUpgradeSlots.reduce(
      (sum, slot) => sum + Number(slot.price || 0),
      0
    );

    const newParkingCost = upgradedCost;
    // Parking cost for new slot
    const newBsp = originalBsp + newParkingCost; // New base price (including new parking cost)
    const newSdr = newBsp * 0.07; // SDR (7%) for new base
    const newGst = newBsp * 0.05; // GST (5%) for new base
    const newTotal = newBsp + newSdr + newGst; // New total after change

    // Difference between old and new total
    const diff = newTotal - oldTotal;

    // Step 7: Return the final pricing, including old and new costs, and the pricing type
    getOldParkingcase(); // Ensure you run the function to check for upgrades
    return {
      case: pricingTypeNew, // "included" or "upgrade"
      parkingNumber: newSlot.number, // Selected parking number
      bsp: newBsp, // Base Selling Price
      sdr: newSdr, // SDR (7%)
      gst: newGst, // GST (5%)
      totalCost: newTotal, // Total cost after parking change
      oldTotal, // Old total before the change
      diff, // Difference between old and new total
      allottedParkingCost, // Old parking cost (0 for included, actual price for upgraded)
      newParkingCost,
      oldBsp: oldBsp,
      oldSdr: oldSdr,
      oldGst: oldGst, // New parking cost (0 for included, actual price for upgraded)
    };
  };

  const handleReset = () => {
    setSelectedIncludedSlots([]);
    setSelectedUpgradeSlots([]);
    setIsSummaryChecked(false);
  };

  const fetchAllottedParking = async (allotmentPaymentId) => {
    try {
      const { data, error } = await supabase
        .from("ParkingSelection")
        .select(
          `
        parkingId,
        parkingInventoryId,
        ParkingInventory (
          parkingInventoryId,
          parkingNumber,
          parkingStatusId,
          ParkingTypes (parkingType)
        )
      `
        )
        .eq("allotmentPaymentId", allotmentPaymentId);

      if (error) throw error;

      if (!data || data.length === 0) {
        return [];
      }

      // Normalize into flat array
      return data
        .filter((d) => d.ParkingInventory)
        .map((d) => d.ParkingInventory);
    } catch (err) {
      console.error("❌ Error fetching allotted parking:", err);
      return [];
    }
  };

  const handleConfirm = async () => {
    try {
      const now = Date.now();

      // 🔹 Collect all selected parkings
      const allSelectedSlots = [
        ...selectedIncludedSlots,
        ...selectedUpgradeSlots,
      ];

      if (!allSelectedSlots.length) {
        alert("Please select at least one parking slot.");
        return;
      }

      // ------------------------------------------------------------------
      // 1️⃣ FINAL AVAILABILITY CHECK (ParkingInventory)
      // ------------------------------------------------------------------
      for (const slot of allSelectedSlots) {
        const { data, error } = await supabase
          .from("ParkingInventory")
          .select("parkingStatusId")
          .eq("parkingInventoryId", slot.id)
          .single();

        if (error) {
          console.error("Availability check error:", error);
          alert("Error checking parking availability.");
          return;
        }

        if (data?.parkingStatusId !== 0) {
          setConflictParkingNumber(slot.number);
          setShowAlreadyAllottedDialog(true);
          handleReset();
          return;
        }
      }

      // ------------------------------------------------------------------
      // 2️⃣ 🔥 RPC CHECK FOR UPGRADE AVAILABILITY (CRITICAL)
      // ------------------------------------------------------------------
      if (selectedUpgradeSlots.length > 0) {
        const { data: rpcData, error: rpcError } = await supabase.rpc(
          "fetch_upgradable_parking_slots_final222222",
          {
            project_id: projectId,
            wing_id: wingId,
            unit_id: unitId,
          }
        );

        if (rpcError) {
          console.error("RPC error:", rpcError);
          setUpgradeUnavailableParkingName(
            selectedUpgradeSlots.map((s) => s.number).join(", ")
          );
          setShowUpgradeUnavailableDialog(true);
          return;
        }

        const allowedUpgradeTypeIds = (rpcData || []).map(
          (r) => r.parkingtypeid
        );

        // ❌ Block if ANY selected upgraded parking is not allowed
        for (const slot of selectedUpgradeSlots) {
          if (!allowedUpgradeTypeIds.includes(slot.parkingTypeId)) {
            setUpgradeUnavailableParkingName(slot.number);
            setShowUpgradeUnavailableDialog(true);
            handleReset();
            return;
          }
        }
      }

      // ------------------------------------------------------------------
      // 3️⃣ RELEASE OLD PARKING(S)
      // ------------------------------------------------------------------
      const { data: oldSelections, error: oldFetchError } = await supabase
        .from("ParkingSelection")
        .select("parkingInventoryId, parkingId")
        .eq("allotmentPaymentId", allotmentPaymentId);

      if (oldFetchError) throw oldFetchError;

      for (const old of oldSelections || []) {
        await supabase
          .from("ParkingInventory")
          .update({ parkingStatusId: 0 })
          .eq("parkingInventoryId", old.parkingInventoryId);

        await supabase
          .from("ParkingSelection")
          .delete()
          .eq("parkingId", old.parkingId);
      }

      // ------------------------------------------------------------------
      // 4️⃣ INSERT NEW PARKING SELECTIONS
      // ------------------------------------------------------------------
      for (const slot of allSelectedSlots) {
        await supabase.from("ParkingSelection").insert({
          allotmentPaymentId,
          parkingInventoryId: slot.id,
          parkingTypeId: slot.parkingTypeId,
          created_at: now,
          last_updated_at: now,
          version: 1,
        });

        await supabase
          .from("ParkingInventory")
          .update({ parkingStatusId: 1 })
          .eq("parkingInventoryId", slot.id);
      }

      // ------------------------------------------------------------------
      // 5️⃣ UPDATE INVENTORY (BACKWARD COMPATIBLE)
      // ------------------------------------------------------------------
      await supabase
        .from("Inventory")
        .update({
          selectedParkingTypeId: allSelectedSlots[0]?.parkingTypeId || null,
          last_updated_at: now,
        })
        .eq("projectId", projectId)
        .eq("wingId", wingId)
        .eq("unitId", unitId);

      // ------------------------------------------------------------------
      // 6️⃣ PRICING (UNCHANGED FORMULA)
      // ------------------------------------------------------------------
      const pricing = calculatePricing();

      await supabase
        .from("AllotmentPayment")
        .update({
          unitCost: {
            total: Math.round(pricing.totalCost),
            bsp: Math.round(pricing.bsp),
            sdr: Math.round(pricing.sdr),
            gst: Math.round(pricing.gst),
          },
          isParkingSelectedBySales: true,
          last_updated_at: now,
        })
        .eq("allotmentPaymentId", allotmentPaymentId);

      await supabase
        .from("Inventory")
        .update({
          totalCost: Math.round(pricing.totalCost),
          BSP: Math.round(pricing.bsp),
          SDR: Math.round(pricing.sdr),
          GST: Math.round(pricing.gst),
          last_updated_at: now,
        })
        .eq("projectId", projectId)
        .eq("wingId", wingId)
        .eq("unitId", unitId);

      // ------------------------------------------------------------------
      // 7️⃣ LEAD LOGS (MULTI-PARKING MESSAGE)
      // ------------------------------------------------------------------
      const leadId = allotment?.leadid || allotment?.leadId;
      const projectName =
        allotment?.projectname || allotment?.projectName || "";
      const unitNumber = allotment?.unitnumber || allotment?.unitNumber || "";
      const userName = allotment?.userName || "SuperAdmin";

      const parkingList = allSelectedSlots
        .map((p) => `#${p.number}`)
        .join(", ");

      const message =
        `Parking selected\n` +
        `Parking ${parkingList} selected by ${userName} ` +
        `for allotment unit ${unitNumber} of project ${projectName}.\n` +
        new Date().toLocaleString("en-US");

      const { data: existingLogs } = await supabase
        .from("LeadLogs")
        .select("leadLogId")
        .eq("allotmentPaymentId", allotmentPaymentId)
        .eq("leadId", leadId)
        .limit(1);

      if (existingLogs?.length) {
        await supabase
          .from("LeadLogs")
          .update({
            message,
            last_updated_at: now,
          })
          .eq("leadLogId", existingLogs[0].leadLogId);
      } else {
        await supabase.from("LeadLogs").insert({
          leadId,
          allotmentPaymentId,
          message,
          leadStatus: "Parking Selected",
          created_at: now,
          last_updated_at: now,
        });
      }

      // ------------------------------------------------------------------
      // 8️⃣ FINAL RESET & CLOSE
      // ------------------------------------------------------------------
      handleReset();
      onClose();
    } catch (err) {
      console.error("❌ Error during parking confirmation:", err);
      alert("Error confirming parking. Please try again.");
    }
  };

  const handleIncludedSelect = (slot) => {
    const totalSelected =
      selectedIncludedSlots.length + selectedUpgradeSlots.length;

    // 🔁 Toggle OFF
    if (selectedIncludedSlots.some((s) => s.id === slot.id)) {
      setSelectedIncludedSlots((prev) => prev.filter((s) => s.id !== slot.id));
      return;
    }

    // 🚫 TOTAL limit reached
    if (totalSelected >= parkingRules.maxIncluded) {
      setShowBlockIncludedDialog(true);
      return;
    }

    // ✅ ALLOW
    setSelectedIncludedSlots((prev) => [...prev, slot]);
  };

  const handleParkingSelection = (number) => {
    // 🚫 If upgraded parking is already selected, block included selection
    // 🚫 If upgraded parking already selected → block included
    // if (selectedUpgradeNumber) {
    //   setShowBlockIncludedDialog(true);
    //   return;
    // }

    const currentConfirmed = confirmedParkingNumber;
    const hasAllottedParking = allottedParking.length > 0;

    // 🧠 CASE 1: No allotted parking yet → directly select (no confirmation dialog)
    if (!hasAllottedParking) {
      setSelectedParkingNumber(number);
      setSelectedUpgradeNumber("");
      setDirectUpgrade(false);

      setTimeout(() => {
        if (pricingRef.current) {
          pricingRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
      return;
    }

    // 🧠 CASE 2: Already allotted → show confirm only if changing
    if (currentConfirmed && currentConfirmed !== number) {
      setOldParkingNumber(currentConfirmed);
      setNewParkingNumber(number);
      setDirectUpgrade(false);
      setShowConfirmDialog(true);
    } else {
      // same parking clicked again → just select silently
      setSelectedParkingNumber(number);
      setSelectedUpgradeNumber("");
      setDirectUpgrade(false);
      setTimeout(() => {
        if (pricingRef.current) {
          pricingRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    }
  };

  const getPaginatedSlots = (slots, page) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return slots.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const paginatedSlots = getPaginatedSlots(parkingSlots, currentPage);
  const includedParkingCount = parkingSlots.length;
  const upgradedParkingCount = upgradedParkingSlots.length;

  const pricing = calculatePricing();
  const selectedSlot = getSelectedSlot();
  console.log("🎯 allottedParking state:", allottedParking);
  const includedPaginatedSlots = getPaginatedSlots(parkingSlots, includedPage);
  const upgradedPaginatedSlots = getPaginatedSlots(
    upgradedParkingSlots,
    upgradedPage
  );

  // Group upgraded slots by floorName
  const upgradedByFloor = upgradedParkingSlots.reduce((acc, slot) => {
    const floor = slot.floorName || "Unknown";
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(slot);
    return acc;
  }, {});
  const sortedFloors = Object.keys(upgradedByFloor).sort(
    (a, b) =>
      (FLOOR_INDEX_MAP[a.toLowerCase()] ?? 999) -
      (FLOOR_INDEX_MAP[b.toLowerCase()] ?? 999)
  );

  // Flatten grouped slots back into an ordered list WITH floor preserved
  const upgradedFlattened = sortedFloors.flatMap((floor) =>
    upgradedByFloor[floor].map((slot) => ({
      ...slot,
      __floor: floor, // keep original floor name
    }))
  );

  // Paginate upgraded slots (12 per page)
  const UPGRADED_ITEMS_PER_PAGE = 12;

  const paginatedUpgraded = upgradedFlattened.slice(
    (upgradedPage - 1) * UPGRADED_ITEMS_PER_PAGE,
    upgradedPage * UPGRADED_ITEMS_PER_PAGE
  );
  // Re-group the paginated items by floor
  const upgradedPageGrouped = paginatedUpgraded.reduce((acc, slot) => {
    const floor = slot.__floor;
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(slot);
    return acc;
  }, {});

  const groupedIncludedParkingByFloor = parkingSlots.reduce((acc, slot) => {
    console.log("slot", slot);

    const floor = slot.floorNumber || "Unknown"; // Group by floor name

    console.log("floorNummwe=er ", floor);

    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(slot);
    return acc;
  }, {});

  const sortedIncludedFloors = Object.keys(groupedIncludedParkingByFloor).sort(
    (a, b) =>
      (FLOOR_INDEX_MAP[a.toLowerCase()] ?? 999) -
      (FLOOR_INDEX_MAP[b.toLowerCase()] ?? 999)
  );

  // Flatten grouped slots back into an ordered list with floor preserved
  const includedFlattened = sortedIncludedFloors.flatMap((floor) =>
    groupedIncludedParkingByFloor[floor].map((slot) => ({
      ...slot,
      __floor: floor, // keep original floor name
    }))
  );

  // Paginate included slots (12 per page)
  const INCLUDED_ITEMS_PER_PAGE = 12;

  const paginatedIncluded = includedFlattened.slice(
    (includedPage - 1) * INCLUDED_ITEMS_PER_PAGE,
    includedPage * INCLUDED_ITEMS_PER_PAGE
  );

  // Re-group the paginated items by floor
  const includedPageGrouped = paginatedIncluded.reduce((acc, slot) => {
    const floor = slot.__floor;
    if (!acc[floor]) acc[floor] = [];
    acc[floor].push(slot);
    return acc;
  }, {});

  // 🔑 True only when:
  // 1. Old parking exists
  // 2. Old parking was INCLUDED
  // 3. New selected parking is INCLUDED
  const isIncludedToIncluded =
    allottedParking &&
    oldParkingCase === "included" &&
    selectedSlot &&
    !selectedUpgradeNumber;

  // 🔹 Button visibility control (FINAL)
  const hasAnySelection =
    selectedIncludedSlots.length > 0 || selectedUpgradeSlots.length > 0;

  // 🔹 Only upgrades require pricing checkbox
  const requiresSummaryConfirmation = selectedUpgradeSlots.length > 0;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#f4f4f9", // Set background color for the modal
          borderRadius: "12px", // Rounded corners for the modal
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "2rem", fontWeight: 600 }}>
        Parking Selection Flow
        <IconButton onClick={onClose} sx={{ float: "right" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={60} />
          </Box>
        ) : parkingSlots.length === 0 ? (
          <Typography align="center" color="text.secondary" mt={2}>
            No Available Parkings Found
          </Typography>
        ) : (
          <>
            {/* ✅ Currently Allotted Parking Section */}
            <AnimatePresence>
              {allottedParking.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      mb: 3,
                      borderRadius: 3,
                      backgroundColor: "#f5f5f5",
                      boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.1)",
                      border: "1px solid #e0e0e0",
                      p: 3,
                    }}
                  >
                    {/* Header */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={3}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          fontSize: "2rem",
                        }}
                      >
                        Allotted Parking
                      </Typography>
                      <CheckCircle color="success" sx={{ fontSize: "3rem" }} />
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* 🔹 MULTI PARKING LIST */}
                    <Box>
                      {allottedParking.map((parking, index) => (
                        <Box
                          key={parking.parkingInventoryId}
                          mb={index < allottedParking.length - 1 ? 3 : 0}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "1.6rem",
                              fontWeight: 600,
                              color: "#333333",
                              mb: 1,
                            }}
                          >
                            <strong>Parking {index + 1}:</strong>{" "}
                            <span style={{ color: "#1976d2", fontWeight: 700 }}>
                              #{parking.parkingNumber}
                            </span>
                          </Typography>

                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "1.4rem",
                              fontWeight: 500,
                              color: "#555555",
                              mb: 1,
                            }}
                          >
                            <strong>Parking Name:</strong>{" "}
                            <span style={{ color: "#1976d2", fontWeight: 600 }}>
                              {parking.ParkingTypes?.parkingType || "-"}
                            </span>
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "1.3rem",
                              fontWeight: 500,
                              color: "#888888",
                            }}
                          >
                            <strong>Status:</strong>{" "}
                            <span style={{ fontWeight: 600 }}>Allotted</span>
                          </Typography>

                          {/* Divider between parkings */}
                          {index < allottedParking.length - 1 && (
                            <Divider sx={{ mt: 2 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 🌟 Included Parking Count Display */}
            <Box display="flex" gap={3} mb={3}>
              {/* Included Parking Count */}
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor: "#e3f2fd",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "1.8rem", fontWeight: 700, color: "#0d47a1" }}
                >
                  Included Parking
                </Typography>

                <Typography
                  sx={{
                    fontSize: "2.6rem",
                    fontWeight: 800,
                    color: "#0d47a1",
                    mt: 1,
                  }}
                >
                  {includedParkingCount}
                </Typography>
              </Box>

              {/* Upgraded Parking Count */}
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor: "#fff3e0",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "1.8rem", fontWeight: 700, color: "#e65100" }}
                >
                  Upgradable Parking
                </Typography>

                <Typography
                  sx={{
                    fontSize: "2.6rem",
                    fontWeight: 800,
                    color: "#e65100",
                    mt: 1,
                  }}
                >
                  {upgradedParkingCount}
                </Typography>
              </Box>
            </Box>

            {/* 🅿️ Parking Selection Grid */}
            <AnimatePresence>
              <Typography
                sx={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  mb: 1,
                  // Blue color for floor names
                }}
              >
                Option 1: Included Parking
              </Typography>
              {Object.keys(includedPageGrouped).map((floor) => (
                <Box key={floor} mb={4}>
                  <Divider sx={{ mb: 2 }} />

                  {/* Floor Heading */}
                  <Typography
                    sx={{
                      fontSize: "2rem",
                      fontWeight: 700,
                      mb: 1,
                      color: "#1976d2", // Blue color for floor names
                    }}
                  >
                    {getFloorLabel(floor)} {/* Display floor name */}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  {/* Parking Slots for the floor */}
                  <Grid container spacing={3}>
                    {includedPageGrouped[floor].map((slot, index) => (
                      <Grid item xs={12} sm={6} md={3} key={slot.id}>
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          whileHover={{ scale: 1.05 }}
                        >
                          <Card
                            sx={{
                              cursor: "pointer",
                              border: selectedIncludedSlots.some(
                                (s) => s.id === slot.id
                              )
                                ? "2px solid #1976d2"
                                : "2px solid #ddd", // Light border
                              borderRadius: "12px", // Rounded corners for a modern look
                              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                              transition: "all 0.3s ease", // Smooth transition on hover
                              "&:hover": {
                                boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
                                transform: "scale(1.02)", // Slight zoom effect on hover
                              },
                              backgroundColor:
                                selectedParkingNumber === slot.number
                                  ? "#f5f5f5" // Soft blue-gray background for selected
                                  : "#f5f5f5", // Very light gray for unselected
                              // Light blue background on selection
                            }}
                            onClick={() => handleIncludedSelect(slot)}
                            // Select parking slot
                          >
                            <CardContent>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={1}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "2rem",
                                    fontWeight: 600,
                                    color: "#333",
                                  }}
                                >
                                  #{slot.number} {/* Display parking number */}
                                </Typography>

                                {selectedIncludedSlots.some(
                                  (s) => s.id === slot.id
                                ) && <CheckCircle color="primary" />}
                              </Box>

                              <Chip
                                label={slot.type} // Show parking type
                                color={getTypeColor(slot.type)} // Set color based on parking type
                                size="small"
                                sx={{
                                  mt: 2,
                                  fontSize: "1.4rem",
                                  fontWeight: 700,
                                  py: 1,
                                  borderRadius: "16px", // Rounded corners for the chip
                                }}
                              />

                              <Typography
                                sx={{
                                  mt: 2,
                                  fontSize: "1.4rem",
                                  fontWeight: 500,
                                  color: "#555",
                                }}
                              >
                                Floor: {getFloorLabel(slot.__floor)}{" "}
                                {/* Display floor name */}
                              </Typography>

                              <Typography
                                color="text.secondary"
                                variant="h6"
                                mt={2}
                                sx={{ fontWeight: 600, fontSize: "1.6rem" }}
                              >
                                {/* ₹{slot.price.toLocaleString("en-IN")} */}
                                Included
                              </Typography>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}

              {/* Pagination for Included Slots */}
              <Box display="flex" justifyContent="center" mt={3} mb={3}>
                <Pagination
                  count={Math.ceil(
                    includedFlattened.length / INCLUDED_ITEMS_PER_PAGE
                  )}
                  page={includedPage}
                  onChange={(_, v) => setIncludedPage(v)}
                />
              </Box>
            </AnimatePresence>
          </>
        )}

        {/* 🔶 Upgraded Parking Slots Section */}
        <>
          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 700,
              mb: 1,
              // Blue color for floor names
            }}
          >
            Option 2: Upgradable Parking
          </Typography>

          <Divider sx={{ mb: 2 }} />
          {Object.keys(upgradedPageGrouped).map((floor) => (
            <Box key={floor} mb={4}>
              {/* Floor Heading */}
              <Typography
                sx={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  mb: 1,
                  color: "#1976d2",
                }}
              >
                {getFloorLabel(floor)}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={3}>
                {upgradedPageGrouped[floor].map((slot, index) => (
                  <Grid item xs={12} sm={6} md={3} key={slot.id}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <Card
                        sx={{
                          cursor: "pointer",
                          border: selectedUpgradeSlots.some(
                            (s) => s.id === slot.id
                          )
                            ? "2px solid #1976d2"
                            : "2px solid #ddd", // Light border
                          borderRadius: "12px", // Rounded corners for a modern look
                          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
                          transition: "all 0.3s ease", // Smooth transition on hover
                          "&:hover": {
                            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.15)",
                            transform: "scale(1.02)", // Slight zoom effect on hover
                          },
                          backgroundColor:
                            selectedParkingNumber === slot.number
                              ? "#bbdefb" // Soft blue-gray background for selected
                              : "#f5f5f5", // Light blue background on selection
                        }}
                        onClick={() => {
                          // 1️⃣ Try selecting
                          const allowed = handleUpgradeSelect(slot);

                          // ⛔ STOP if blocked (THIS FIXES YOUR BUG)
                          if (!allowed) return;

                          // 2️⃣ Now safe to run confirm logic
                          const hasAllottedParking = allottedParking.length > 0;

                          const isAlreadyAllotted = allottedParking.some(
                            (p) => p.parkingNumber === slot.number
                          );

                          // 🧪 First-time selection → no confirm
                          if (!hasAllottedParking || isAlreadyAllotted) {
                            setSelectedUpgradeNumber(slot.number);
                            setSelectedParkingNumber("");
                            setDirectUpgrade(true);
                            return;
                          }

                          // 🧪 ACTUAL CHANGE → show confirm
                          setOldParkingNumber(
                            allottedParking
                              .map((p) => p.parkingNumber)
                              .join(", ")
                          );
                          setNewParkingNumber(slot.number);
                          setDirectUpgrade(true);
                          setShowConfirmDialog(true);
                        }}
                      >
                        <CardContent>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={1}
                          >
                            <Typography
                              sx={{
                                fontSize: "2rem",
                                fontWeight: 600,
                                color: "#333",
                              }}
                            >
                              #{slot.number} {/* Display parking number */}
                            </Typography>

                            {selectedUpgradeSlots.some(
                              (s) => s.id === slot.id
                            ) && <CheckCircle color="primary" />}
                          </Box>

                          <Chip
                            label={slot.type} // Show parking type
                            color="primary"
                            size="small"
                            sx={{
                              mt: 2,
                              fontSize: "1.4rem",
                              fontWeight: 700,
                              py: 1,
                              borderRadius: "16px", // Rounded corners for the chip
                            }}
                          />

                          <Typography
                            sx={{
                              mt: 2,
                              fontSize: "1.4rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            Floor: {getFloorLabel(slot.__floor)}{" "}
                            {/* Display floor name */}
                          </Typography>

                          <Typography
                            color="text.secondary"
                            variant="h6"
                            mt={2}
                            sx={{ fontWeight: 600, fontSize: "1.6rem" }}
                          >
                            ₹{slot.price.toLocaleString("en-IN")}{" "}
                            {/* Display price */}
                          </Typography>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          {/* Pagination for Upgraded Slots */}
          <Box display="flex" justifyContent="center" mt={3} mb={3}>
            <Pagination
              count={Math.ceil(
                upgradedFlattened.length / UPGRADED_ITEMS_PER_PAGE
              )}
              page={upgradedPage}
              onChange={(_, v) => setUpgradedPage(v)}
            />
          </Box>
        </>

        {/* 💰 Pricing Summary */}
        <AnimatePresence>
          {/* {(selectedSlot || selectedUpgradeNumber) && (
            (allottedParking || pricing.case !== "included") && */}

          {(selectedSlot || selectedUpgradeNumber) &&
            !isIncludedToIncluded &&
            (allottedParking || pricing.case !== "included") && (
              <motion.div
                ref={pricingRef}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 30 }}
                transition={{ duration: 0.4 }}
              >
                <Box mb={3} p={4} border="2px solid #bbb" borderRadius={3}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: "2.4rem",
                      fontWeight: 700,
                      mb: 2,
                      textAlign: "center",
                    }}
                  >
                    Pricing Summary
                  </Typography>

                  {/* 🧮 Previous Total */}
                  {/* Show old totals ONLY if user already had a parking allotted */}
                  {allottedParking && allottedParking.parkingStatusId === 1 && (
                    <>
                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography
                          sx={{ fontSize: "1.5rem", fontWeight: "100" }}
                        >
                          Agreement Value
                        </Typography>

                        <span style={{ fontSize: "1.5rem", fontWeight: "100" }}>
                          ₹{pricing.oldBsp?.toLocaleString("en-IN") || 0}
                        </span>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography
                          sx={{ fontSize: "1.2rem", fontWeight: "100" }}
                        >
                          Old SDR
                        </Typography>

                        <span style={{ fontSize: "1.2rem", fontWeight: "100" }}>
                          ₹{pricing.oldSdr?.toLocaleString("en-IN") || 0}
                        </span>
                      </Box>

                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography
                          sx={{ fontSize: "1.2rem", fontWeight: "100" }}
                        >
                          Old GST
                        </Typography>

                        <span style={{ fontSize: "1.2rem", fontWeight: "100" }}>
                          ₹{pricing.oldGst?.toLocaleString("en-IN") || 0}
                        </span>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mt={1}>
                        <Typography
                          sx={{ fontSize: "1.7rem", fontWeight: 600 }}
                        >
                          Previous Total Unit Cost
                        </Typography>

                        <span
                          style={{
                            fontSize: "1.8rem",
                            color: "#1976d2",
                            fontWeight: "800",
                          }}
                        >
                          ₹{pricing.oldTotal?.toLocaleString("en-IN") || 0}
                        </span>
                      </Box>

                      <Typography
                        sx={{
                          fontSize: "1.2rem",
                          fontWeight: "100",
                          ml: { xs: 2, sm: 25, md: 50 }, // Adjust margin for mobile, tablet, and desktop views
                          mt: 1,
                          fontSize: { xs: "1.2rem", sm: "1.2rem" }, // Adjust font size for mobile
                        }}
                      >
                        {`(Total is calculated as:    Agreement Value + Old SDR + Old GST)`}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      {/* <Box display="flex" justifyContent="space-between" mt={1}>
                      <Typography sx={{ fontSize: "1.8rem", fontWeight: 600 }}>
                        Included Parking Cost
                      </Typography>
                      <Typography sx={{ fontSize: "1.8rem", fontWeight: 600 }}>
                        ₹{pricing.allottedParkingCost?.toLocaleString("en-IN") || "0"}
                      </Typography>
                    </Box> */}
                    </>
                  )}

                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography sx={{ fontSize: "1.7rem", fontWeight: 600 }}>
                      {pricing.case === "upgrade"
                        ? `Upgraded Parking `
                        : `Included Parking `}
                      cost
                    </Typography>
                    <Typography sx={{ fontSize: "1.7rem", fontWeight: 700 }}>
                      ₹{pricing.newParkingCost?.toLocaleString("en-IN") || "0"}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography sx={{ fontSize: "1.5rem", fontWeight: "100" }}>
                      Agreement Value
                    </Typography>
                    <span style={{ fontSize: "1.5rem", fontWeight: "100" }}>
                      ₹{Math.round(pricing.bsp).toLocaleString("en-IN")}
                    </span>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography sx={{ fontSize: "1.2rem", fontWeight: "100" }}>
                      New SDR
                    </Typography>
                    <span style={{ fontSize: "1.2rem", fontWeight: "100" }}>
                      ₹{Math.round(pricing.sdr).toLocaleString("en-IN")}
                    </span>
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography sx={{ fontSize: "1.2rem", fontWeight: "100" }}>
                      New GST
                    </Typography>
                    <span style={{ fontSize: "1.2rem", fontWeight: "100" }}>
                      ₹{Math.round(pricing.gst).toLocaleString("en-IN")}
                    </span>
                  </Box>
                  {/* 💰 New Final Total */}
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography sx={{ fontSize: "1.7rem", fontWeight: 600 }}>
                      New Total Unit Cost
                    </Typography>

                    <span
                      style={{
                        fontSize: "1.8rem",
                        color: "#1976d2",
                        fontWeight: "800",
                      }}
                    >
                      ₹{Math.round(pricing.totalCost).toLocaleString("en-IN")}
                    </span>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: "100",
                      ml: { xs: 2, sm: 25, md: 50 }, // Adjust margin for mobile, tablet, and desktop views
                      mt: 1,
                      fontSize: { xs: "1.2rem", sm: "1.2rem" }, // Adjust font size for mobile
                    }}
                  >
                    {`(Total is calculated as:    Agreement Value + New SDR + New GST)`}
                  </Typography>
                </Box>
              </motion.div>
            )}
        </AnimatePresence>

        {/* 
         {(selectedSlot || selectedUpgradeNumber) && ((allottedParking || pricing.case !== "included") && */}

        {(selectedSlot || selectedUpgradeNumber) &&
          !isIncludedToIncluded &&
          (allottedParking || pricing.case !== "included") && (
            <Box mt={2} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <input
                type="checkbox"
                checked={isSummaryChecked}
                onChange={(e) => setIsSummaryChecked(e.target.checked)}
                style={{ width: "20px", height: "20px" }}
              />
              <Typography sx={{ fontSize: "1.6rem", fontWeight: 600 }}>
                I have reviewed the pricing summary.
              </Typography>
            </Box>
          )}
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 3, gap: 2 }}>
        {hasAnySelection && (
          <>
            {/* 🔄 RESET */}
            <Button
              onClick={handleReset}
              variant="outlined"
              fullWidth
              sx={{
                fontSize: "1.6rem",
                fontWeight: 600,
                px: 2,
                py: 0.8,
                borderRadius: "12px",
              }}
            >
              Reset
            </Button>

            {/* ✅ CONFIRM */}
            <Button
              onClick={handleConfirm}
              variant="contained"
              fullWidth
              disabled={requiresSummaryConfirmation && !isSummaryChecked}
              color="primary"
              sx={{
                fontSize: "1.6rem",
                fontWeight: 600,
                px: 2,
                py: 0.8,
                borderRadius: "12px",
              }}
            >
              {requiresSummaryConfirmation ? "Confirm Upgrade" : "Confirm"}
            </Button>
          </>
        )}
      </DialogActions>

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "2.5rem",
            fontWeight: 700,
            textAlign: "center",
            color: "#1a237e",
          }}
        >
          Confirm Parking Change
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3 }}>
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              fontSize: "2rem",
              fontWeight: 600,
              color: "#212121",
              lineHeight: 1.6,
            }}
          >
            You are about to release parking number{" "}
            <strong style={{ color: "#d32f2f" }}>#{oldParkingNumber}</strong>{" "}
            (which is already confirmed) and allot a new parking{" "}
            <strong style={{ color: "#2e7d32" }}>#{newParkingNumber}</strong>.
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 3,
              fontSize: "2rem",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Please confirm if you want to proceed with this change.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ ml: "auto", pb: 3 }}>
          <Button
            onClick={() => {
              // ❌ Cancel — close dialog, do nothing
              setShowConfirmDialog(false);
              setOldParkingNumber(null);
              setNewParkingNumber(null);
            }}
            variant="outlined"
            size="small" // Make button small
            sx={{
              fontSize: "1.6rem", // Adjust font size for small button
              fontWeight: 600,
              px: 2, // Adjust padding for smaller button
              py: 0.8,
              borderRadius: "12px", // Rounded corners
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="small" // Make button small
            sx={{
              fontSize: "1.6rem", // Adjust font size for small button
              fontWeight: 700,
              px: 2, // Adjust padding for smaller button
              py: 0.8,
              borderRadius: "12px", // Rounded corners
            }}
            onClick={() => {
              setShowConfirmDialog(false);

              if (directUpgrade) {
                setSelectedUpgradeNumber(newParkingNumber);
              } else {
                setSelectedParkingNumber(newParkingNumber);
              }

              setOldParkingNumber(null);
              setNewParkingNumber(null);

              // ⬇️ Auto-scroll to pricing summary after short delay
              setTimeout(() => {
                if (pricingRef.current) {
                  pricingRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }, 300);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🚫 Block Included Parking when Upgraded is already selected */}
      <Dialog
        open={showBlockIncludedDialog}
        onClose={() => setShowBlockIncludedDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "2.1rem",
            fontWeight: 700,
            textAlign: "center",
            color: "#d32f2f",
          }}
        >
          Cannot Select Included Parking
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3 }}>
          <Typography
            sx={{ fontSize: "1.8rem", fontWeight: 600, textAlign: "center" }}
          >
            Maximum included parking selected.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowBlockIncludedDialog(false)}
            sx={{ fontSize: "1.6rem", fontWeight: 600 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🚫 Block Upgraded Parking when Included is already selected */}
      <Dialog
        open={showBlockUpgradeDialog}
        onClose={() => setShowBlockUpgradeDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "2.1rem",
            fontWeight: 700,
            textAlign: "center",
            color: "#d32f2f",
          }}
        >
          Cannot Select Upgraded Parking
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3 }}>
          <Typography
            sx={{ fontSize: "1.8rem", fontWeight: 600, textAlign: "center" }}
          >
            You can select only <strong>ONE upgraded parking</strong> for this
            unit.
            <br />
            Please reset your selection to change it.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowBlockUpgradeDialog(false)}
            sx={{ fontSize: "1.6rem", fontWeight: 600 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAlreadyAllottedDialog}
        onClose={() => setShowAlreadyAllottedDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "2rem",
            fontWeight: 700,
            textAlign: "center",
            color: "#d32f2f",
          }}
        >
          Parking Slot Unavailable
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 2 }}>
          <Typography
            sx={{ fontSize: "1.7rem", fontWeight: 500, textAlign: "center" }}
          >
            Parking <strong>#{conflictParkingNumber}</strong> has already been
            allotted to someone else. <br />
            Please select another slot.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            variant="contained"
            onClick={async () => {
              setShowAlreadyAllottedDialog(false);
              setConflictParkingNumber(null);

              // 🔥 REFRESH available slots so the card disappears
              await fetchParkingSlots();
            }}
            sx={{ fontSize: "1.6rem", fontWeight: 600 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUpgradeUnavailableDialog}
        onClose={() => setShowUpgradeUnavailableDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px 4px",
            width: "360px",
            maxWidth: "90%",
            bgcolor: "#fff",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            textAlign: "center",
            paddingBottom: 0,
            color: "#d32f2f",
          }}
        >
          Upgrade Parking Unavailable
        </DialogTitle>

        <DialogContent
          sx={{
            marginTop: "8px",
            paddingX: "20px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              color: "#444",
              lineHeight: 1.5,
            }}
          >
            Upgrade parking is <strong>not allowed</strong> for this unit.
            <br />
            Please select <strong>included parking only</strong>.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            paddingBottom: "10px",
          }}
        >
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              paddingX: "24px",
              borderRadius: "10px",
            }}
            onClick={async () => {
              setShowUpgradeUnavailableDialog(false);
              await fetchParkingSlots();
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default function ParkingModalWithTheme(props) {
  return (
    <ThemeProvider theme={theme}>
      <ParkingModal {...props} />
    </ThemeProvider>
  );
}
